import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LocationPicker.css';

const DEFAULT_CENTER = [33.3152, 44.3661];

const parseCoordinates = (latitude, longitude) => {
  const parsedLatitude = Number(latitude);
  const parsedLongitude = Number(longitude);

  if (
    latitude === '' || longitude === '' ||
    !Number.isFinite(parsedLatitude) || !Number.isFinite(parsedLongitude) ||
    parsedLatitude < -90 || parsedLatitude > 90 ||
    parsedLongitude < -180 || parsedLongitude > 180
  ) {
    return null;
  }

  return [parsedLatitude, parsedLongitude];
};

const LocationPicker = ({ address, latitude, longitude, onClose, onSelect }) => {
  const mapElementRef = useRef(null);
  const selectedPositionRef = useRef(DEFAULT_CENTER);
  const [locationStatus, setLocationStatus] = useState('');

  useEffect(() => {
    const enteredCoordinates = parseCoordinates(latitude, longitude);
    const startingPosition = enteredCoordinates || DEFAULT_CENTER;
    const map = L.map(mapElementRef.current).setView(startingPosition, enteredCoordinates ? 15 : 6);
    const marker = L.marker(startingPosition, {
      draggable: true,
      icon: L.divIcon({ className: 'conference-map-marker' }),
    }).addTo(map);
    selectedPositionRef.current = startingPosition;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    const updatePosition = (position) => {
      selectedPositionRef.current = [position.lat, position.lng];
      marker.setLatLng(selectedPositionRef.current);
    };

    map.on('click', (event) => updatePosition(event.latlng));
    marker.on('dragend', (event) => updatePosition(event.target.getLatLng()));

    let requestController;
    if (enteredCoordinates) {
      setLocationStatus('Showing the entered coordinates.');
    } else if (address.trim()) {
      setLocationStatus('Finding the entered location...');
      requestController = new AbortController();
      const searchUrl = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(address.trim())}`;

      fetch(searchUrl, { signal: requestController.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Location search failed');
          return response.json();
        })
        .then((results) => {
          if (!results.length) {
            setLocationStatus('Location not found. Choose a point on the map.');
            return;
          }

          const foundPosition = [Number(results[0].lat), Number(results[0].lon)];
          selectedPositionRef.current = foundPosition;
          marker.setLatLng(foundPosition);
          map.setView(foundPosition, 15);
          setLocationStatus('Showing the entered location.');
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            setLocationStatus('Could not find the location. Choose a point on the map.');
          }
        });
    } else {
      setLocationStatus('Choose a point on the map.');
    }

    return () => {
      requestController?.abort();
      map.remove();
    };
  }, [address, latitude, longitude]);

  const handleConfirm = () => {
    onSelect(selectedPositionRef.current[0], selectedPositionRef.current[1]);
    onClose();
  };

  return (
    <div className="conference-map-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="conference-map-dialog" role="dialog" aria-modal="true" aria-labelledby="conference-map-title">
        <header className="conference-map-dialog__header">
          <h2 id="conference-map-title">Set conference location</h2>
          <button type="button" className="conference-map-dialog__close" onClick={onClose} aria-label="Close map">&times;</button>
        </header>
        <p className="conference-map-dialog__status" role="status">{locationStatus}</p>
        <div className="conference-map" ref={mapElementRef} />
        <footer className="conference-map-dialog__footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-info" onClick={handleConfirm}>Use this location</button>
        </footer>
      </section>
    </div>
  );
};

export default LocationPicker;